export const PDP_EVENT_KIND = {
  HW_LINKUP: 'HW_LINKUP',
  OG_EVENT: 'OG_EVENT',
};

export const CAROUSEL_CARD_TYPE = {
  LINKUPS: 'linkups',
  OG_EVENT: 'og_event',
};

export function mapEventKindToCarouselCardType(kind) {
  if (kind === PDP_EVENT_KIND.OG_EVENT) {
    return CAROUSEL_CARD_TYPE.OG_EVENT;
  }

  return CAROUSEL_CARD_TYPE.LINKUPS;
}

export function toCarouselPositionClicked(zeroBasedIndex) {
  if (!Number.isInteger(zeroBasedIndex)) {
    return null;
  }

  return zeroBasedIndex + 1;
}

export function buildOccasionGeniusEventClickParams(event) {
  if (!event?.isOgEvent && event?.kind !== PDP_EVENT_KIND.OG_EVENT) {
    return {};
  }

  const occasionGeniusEventId = event?.eventId ?? event?.id;

  return {
    occasion_genius_event_id: occasionGeniusEventId != null ? String(occasionGeniusEventId) : '',
    occasion_genius_event_name: event?.name || '',
  };
}

export function buildCarouselCardInteractionTracking(event, zeroBasedIndex) {
  const eventKind = event?.kind || PDP_EVENT_KIND.HW_LINKUP;

  return {
    carousel_card_type: mapEventKindToCarouselCardType(eventKind),
    carousel_position_clicked: toCarouselPositionClicked(zeroBasedIndex),
    event_kind: eventKind,
    ...buildOccasionGeniusEventClickParams(event),
  };
}

function resolveOgEventImageUrl(ogEvent) {
  if (ogEvent?.imageUrl) {
    return ogEvent.imageUrl;
  }

  const ogImages = ogEvent?.ogMetadata?.images;
  if (Array.isArray(ogImages) && ogImages.length > 0) {
    const largeImage = ogImages.find((image) => image?.size === 'large');
    return largeImage?.url || ogImages[0]?.url || '';
  }

  return '';
}

function compareEventsByStartTime(eventA, eventB) {
  const timeA = eventA?.startTime ? Date.parse(eventA.startTime) : 0;
  const timeB = eventB?.startTime ? Date.parse(eventB.startTime) : 0;

  if (Number.isNaN(timeA)) {
    return Number.isNaN(timeB) ? 0 : 1;
  }

  if (Number.isNaN(timeB)) {
    return -1;
  }

  return timeA - timeB;
}

const OG_GATHERING_PRIORITY_BUCKET = 10;
const MAX_OG_GATHERINGS_PER_START_TIME = 2;
const MAX_OG_GATHERINGS_CARDS = 10;

function getOgGatheringPriorityScore(event) {
  let score = 0;

  if (event?.priorityBucket === OG_GATHERING_PRIORITY_BUCKET) {
    score += 4;
  }

  if (event?.ogMetadata?.isTravelWorthy) {
    score += 2;
  }

  if (event?.ogMetadata?.isStaffPick) {
    score += 1;
  }

  return score;
}

export function sortAndLimitOgGatherings(items = []) {
  if (!Array.isArray(items) || !items.length) {
    return [];
  }

  // Safe fallback to prevent legacy browser crashes
  const sortedItems = [...items].filter(Boolean).sort(
    (itemA, itemB) => getOgGatheringPriorityScore(itemB) - getOgGatheringPriorityScore(itemA),
  );
  const calendarDayCounts = new Map();

  return sortedItems.filter((item) => {
    // Extract YYYY-MM-DD calendar day from "YYYY-MM-DDTHH:mm:ss"
    const calendarDayKey = item?.startTime ? String(item.startTime).slice(0, 10) : 'unknown';
    const count = calendarDayCounts.get(calendarDayKey) || 0;

    if (count >= MAX_OG_GATHERINGS_PER_START_TIME) {
      return false;
    }

    calendarDayCounts.set(calendarDayKey, count + 1);
    return true;
  }).slice(0, MAX_OG_GATHERINGS_CARDS);
}

function resolveApiTotalCount(totalCountValue, items) {
  if (totalCountValue != null && Number.isFinite(Number(totalCountValue))) {
    return Number(totalCountValue);
  }

  return items.length;
}

export function useEventsCarousel(options) {
  const {
    linkupsItems,
    ogEventsItems,
    linkupsTotalCount,
    ogEventsTotalCount,
    showOgEventsModule,
    propertyLocation,
    propertyId,
    propertyName,
    propertyPageUrl,
  } = options;

  const { t } = useI18n();
  const dateTime = useDateTime();
  const { useImage } = useImages();
  const { useFormatter } = useFormatters();
  const { useUrl } = useUrls();
  const { getEventPageUrlFromRawEvent } = useEventDetailsModel();
  const languageStore = useLanguageStore();

  const activeEventFilter = ref(null);

  const linkupItemsList = computed(() => toValue(linkupsItems) || []);
  const ogEventsList = computed(() => toValue(ogEventsItems) || []);
  const isOgEventsModuleEnabled = computed(() => toValue(showOgEventsModule) === true);

  const mergedEvents = computed(() => {
    const events = [];

    linkupItemsList.value.forEach((linkup) => {
      events.push({
        kind: PDP_EVENT_KIND.HW_LINKUP,
        startTime: linkup?.startTime,
        rawEvent: linkup,
      });
    });

    if (isOgEventsModuleEnabled.value) {
      ogEventsList.value.forEach((ogEvent) => {
        events.push({
          kind: PDP_EVENT_KIND.OG_EVENT,
          startTime: ogEvent?.startTime,
          rawEvent: ogEvent,
        });
      });
    }

    return events.sort(compareEventsByStartTime);
  });

  const hasLinkupEvents = computed(() => linkupItemsList.value.length > 0);
  const hasOgEvents = computed(() => isOgEventsModuleEnabled.value && ogEventsList.value.length > 0);

  const showFilterPills = computed(() => hasLinkupEvents.value && hasOgEvents.value);

  const filteredEvents = computed(() => {
    if (!activeEventFilter.value) {
      return mergedEvents.value;
    }

    return mergedEvents.value.filter((event) => event.kind === activeEventFilter.value);
  });

  const totalEventCount = computed(() => {
    const linkupsTotal = resolveApiTotalCount(
      toValue(linkupsTotalCount),
      linkupItemsList.value,
    );

    if (!isOgEventsModuleEnabled.value) {
      return linkupsTotal;
    }

    const ogEventsTotal = resolveApiTotalCount(
      toValue(ogEventsTotalCount),
      ogEventsList.value,
    );

    return linkupsTotal + ogEventsTotal;
  });

  const linkupsCountDisplayed = computed(() => linkupItemsList.value.length);

  const ogEventsCountDisplayed = computed(() => {
    if (!isOgEventsModuleEnabled.value) {
      return 0;
    }

    return ogEventsList.value.length;
  });

  const occasionGeniusEventsInCarousel = computed(() => {
    return mergedEvents.value
      .filter((event) => event.kind === PDP_EVENT_KIND.OG_EVENT)
      .map((event) => event.rawEvent?.ogMetadata?.ogId)
      .filter((id) => id != null)
      .join('|');
  });

  const headingPreviewImages = computed(() => {
    const previewLimit = 3;
    const images = [];

    linkupItemsList.value.forEach((linkup) => {
      if (linkup?.imageUrl && images.length < previewLimit) {
        images.push({ picture: linkup.imageUrl.replace('q_auto', 'q_auto,w_40') });
      }
    });

    if (isOgEventsModuleEnabled.value) {
      ogEventsList.value.forEach((ogEvent) => {
        if (images.length >= previewLimit) {
          return;
        }

        const imageUrl = resolveOgEventImageUrl(ogEvent);
        if (imageUrl) {
          images.push({ picture: imageUrl.replace('q_auto', 'q_auto,w_40') });
        }
      });
    }

    return images.slice(0, previewLimit);
  });

  const showEventsCarousel = computed(() => {
    if (isOgEventsModuleEnabled.value) {
      return hasLinkupEvents.value || hasOgEvents.value;
    }

    return hasLinkupEvents.value;
  });

  const carouselEventList = computed(() => filteredEvents.value.map((event) => event.rawEvent));

  const getOgEventPageUrl = (ogEvent) => {
    return getEventPageUrlFromRawEvent(ogEvent, t('t_OGEVENTSSLUG'));
  };

  const getEventSearchDatesUrl = (event) => {
    const location = toValue(propertyLocation);
    const propertyIdValue = toValue(propertyId);
    const propertyNameValue = toValue(propertyName);

    if (!location?.city?.urlFriendlyName || !propertyIdValue || !propertyNameValue) {
      return null;
    }

    const propertyLocationData = {
      propertyId: propertyIdValue,
      urlFriendlyCity: useFormatter.normalizeUrl(location.city.urlFriendlyName),
      urlFriendlyProperty: useFormatter.normalizeUrl(propertyNameValue),
    };

    const formatGatheringDateToFlat = (dateValue) => {
      if (!dateValue) {
        return null;
      }

      if (dateValue?.year) {
        return dateTime.formatToFlat(dateValue);
      }

      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return dateTime.formatToFlat(dateTime.formatToObject(parsedDate));
    };

    const checkInDate = formatGatheringDateToFlat(event?.startTime);
    const checkOutDate = formatGatheringDateToFlat(event?.endTime) || checkInDate;

    if (!checkInDate || !checkOutDate) {
      return null;
    }

    let redirectUrl = useUrl.getPropertyPageUrlDynamic(propertyLocationData, {
      from: checkInDate,
      to: checkOutDate,
      guests: 1,
    });

    redirectUrl += '&origin=microsite';

    return redirectUrl;
  };

  const mapEventToCarouselCard = (eventEntry) => {
    const {
      kind,
      rawEvent,
    } = eventEntry;
    const location = toValue(propertyLocation);
    const pageUrl = toValue(propertyPageUrl);
    const currentLanguage = languageStore.getCurrentLanguage.language;

    const eventStartDay = dateTime.formatToHumanReadableShortDate(rawEvent.startTime, currentLanguage);

    if (kind === PDP_EVENT_KIND.OG_EVENT) {
      const imageUrl = resolveOgEventImageUrl(rawEvent);
      const categoryName = rawEvent?.category?.name || t('t_OGEVENTS');
      const eventId = rawEvent?.ogMetadata?.ogId ?? rawEvent?.eventId ?? null;
      const ogEventPageUrl = getOgEventPageUrl({
        ...rawEvent,
        eventId,
      });

      const ogPrice = rawEvent?.price?.value
        ? useFormatter.formatCurrency(rawEvent.price.value, rawEvent.price.currency)
        : '';

      return {
        id: rawEvent.idGathering,
        eventId,
        kind,
        name: rawEvent.name,
        type: categoryName,
        image: {
          small: useImage.getOptimizedImageUrl(imageUrl, { width: 160 }),
          large: useImage.getOptimizedImageUrl(imageUrl, { width: 480 }),
        },
        images:
          rawEvent?.ogMetadata?.images
            ?.map((image) => image?.url)
            .filter(Boolean) || [],
        date: eventStartDay,
        startTime: rawEvent?.startTime,
        endTime: rawEvent?.endTime,
        price: ogPrice,
        location: {
          cityId: rawEvent?.cityId,
          city: rawEvent?.cityName,
          address: rawEvent?.locationAddress || location?.address?.line1,
          postalCode: location?.address?.postalCode || 'N/A',
          addressRegion: location?.country?.name,
          addressCountry: location?.country?.name,
        },
        geo: {
          latitude: rawEvent?.latitude,
          longitude: rawEvent?.longitude,
        },
        url: ogEventPageUrl || pageUrl,
        moreDetailsUrl: ogEventPageUrl,
        searchDatesUrl: null,
        audience: '',
        description: rawEvent?.description,
        additionalInfo: '',
        isOgEvent: true,
        translations: {
          tHostedBy: t('t_OGEVENTMOREDETAILSFROMORGANISER'),
          tStayHere: '',
          tBookNow: t('t_BOOKABED'),
          tReadMore: t('t_READMORE'),
        },
        dateCreated: rawEvent?.dateCreated,
      };
    }

    const linkupPrice = rawEvent?.price?.value
      ? useFormatter.formatCurrency(rawEvent.price.value, rawEvent.price.currency)
      : '';

    const linkupAudience
      = rawEvent?.participantsType === 1 ? t('t_LINKUPAUDIENCEALLINCITY') : t('t_LINKUPAUDIENCEALLINHOSTEL');
    const searchDatesUrl = getEventSearchDatesUrl(rawEvent);

    return {
      id: rawEvent.idGathering,
      kind,
      name: rawEvent.name,
      type: t('t_LINKUPHOSTELEVENT'),
      image: {
        small: useImage.getOptimizedImageUrl(rawEvent.imageUrl, { width: 160 }),
        large: useImage.getOptimizedImageUrl(rawEvent.imageUrl, { width: 480 }),
      },
      images: rawEvent?.category?.coverImages,
      date: eventStartDay,
      startTime: rawEvent?.startTime,
      endTime: rawEvent?.endTime,
      price: linkupPrice,
      host: {
        name: rawEvent?.hostelOwner?.name,
        image: rawEvent?.hostelOwner?.imageUrl,
      },
      location: {
        cityId: rawEvent?.cityId,
        city: rawEvent?.cityName,
        address: rawEvent?.locationAddress || location?.address?.line1,
        postalCode: location?.address?.postalCode || 'N/A',
        addressRegion: location?.country?.name,
        addressCountry: location?.country?.name,
      },
      geo: {
        latitude: rawEvent?.latitude,
        longitude: rawEvent?.longitude,
      },
      url: pageUrl !== '' ? `${pageUrl}#linkups` : '',
      searchDatesUrl,
      audience: linkupAudience,
      description: rawEvent?.description,
      additionalInfo: t('t_LINKUPADDITIONALINFO'),
      isOgEvent: false,
      translations: {
        tHostedBy: t('t_LINKUPHOSTEDBY'),
        tStayHere: t('t_LINKUPSTAYHERETOJOIN'),
        tBookNow: t('t_BOOKABED'),
        tReadMore: t('t_READMORE'),
      },
      dateCreated: rawEvent?.dateCreated,
    };
  };

  const carouselDisplayEvents = computed(() => filteredEvents.value.map((event) => mapEventToCarouselCard(event)));

  const toggleEventFilter = (kind) => {
    if (activeEventFilter.value === kind) {
      activeEventFilter.value = null;
      return;
    }

    activeEventFilter.value = kind;
  };

  return {
    PDP_EVENT_KIND,
    activeEventFilter,
    mergedEvents,
    filteredEvents,
    carouselEventList,
    carouselDisplayEvents,
    totalEventCount,
    linkupsCountDisplayed,
    ogEventsCountDisplayed,
    occasionGeniusEventsInCarousel,
    headingPreviewImages,
    showFilterPills,
    showEventsCarousel,
    hasLinkupEvents,
    hasOgEvents,
    toggleEventFilter,
    getOgEventPageUrl,
    getEventSearchDatesUrl,
  };
}
